import * as Actions from '../actions/caseTypeActions';
import CaseType from '../../entities/caseType';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CaseTypeAction = { 
    type: string; 
    caseType: CaseType | null; 
    caseTypeId: string | CaseType; 
    partial: boolean; 
    lastFetch: number; 
    caseTypes: CaseType[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<CaseType> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {CaseType[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<CaseType> | null, firmId: string, userId: string, updates?: CaseType[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'caseType', 'caseTypes', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<CaseType>} data
 * @param {CaseType[]} [updates]
 * @returns {ArrayMap<CaseType>}
 */
function updateStorage(data: ArrayMap<CaseType>, updates?: CaseType[]): ArrayMap<CaseType> {
	if (logging) console.debug('⮂ caseTypeReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {CaseType[]} [updates]
 * @returns {ArrayMap<CaseType>}
 */
function updateStorageNoReturn(updates?: CaseType[]): void {
	if (logging) console.debug('⮂ caseTypeReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {CaseType[]} [data]
 * @returns {ArrayMap<CaseType>}
 */
function createState(data?: CaseType[] | ArrayMap<CaseType>): ArrayMap<CaseType> {
	if (!data) return ArrayMapRegistry.clone<CaseType>('caseType', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<CaseType>('caseType', data.map(e => e));
    else return ArrayMapRegistry.clone<CaseType>('caseType', data);
}

function caseTypes(state: ArrayMap<CaseType>, action: CaseTypeAction): ArrayMap<CaseType> {
	if (!state) state = createState();

	let st: ArrayMap<CaseType>;

	switch (action.type) {
		// action.caseType - the new value of caseType with id action.id
		// action.caseTypeId - id of the target, used if new value is null
		case Actions.CASETYPE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ caseTypeReducer: FETCH_ONE_SUCCESS', action);

			if (action.caseType === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.caseTypeId)) {
				st = state.clone();
				st.set(action.caseType);
				// state.clear();

				updateStorageNoReturn([action.caseType]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.caseTypes
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.caseTypes
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.caseTypes
			 !partial, !stateEmpty, fetchEmpty	-> D return caseTypes in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge caseTypes in state newer than those in action.caseTypes
		 */
		case Actions.CASETYPE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.caseTypes.length === 0;
			const fetched = action.caseTypes.map(caseType => new CaseType({...caseType, persisted: true}));

			if (logging) console.debug(`⮂ caseTypeReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} caseTypes with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ caseTypeReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ caseTypeReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<CaseType> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: CaseType[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(caseType => {
					const current = st.get(caseType.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && caseType.status === 'D') {
							// Remove it from state
							toUpdate.push(caseType);
							st.delete(caseType);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (caseType.addedOn > current.addedOn) {
								toUpdate.push(caseType);
								st.set(caseType);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (caseType.status !== 'D') {
							toUpdate.push(caseType);
							st.set(caseType);
						}
						else if (logging) console.debug('⮂ caseTypeReducer: partialMerge: skipping deleted caseType not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ caseTypeReducer: empty: caseTypes', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ caseTypeReducer: fullMerge: caseTypes', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localCaseType => {
                    // If this caseType has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localCaseType.persisted) {
                        const fetchedCaseType = st.get(localCaseType.id);
                        if (!fetchedCaseType || localCaseType.addedOn > fetchedCaseType.addedOn) st.set(localCaseType);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.CASETYPE_FETCH_ONE_FAILURE:
			console.error('CASETYPE_FETCH_ONE_FAILURE', action.caseTypeId, action.err);

			// No update to storage required
			return state;

		case Actions.CASETYPE_FETCH_FAILURE:
			console.error('CASETYPE_FETCH_FAILURE', action.caseTypeId, action.err);

			// No update to storage required
			return state;

		case Actions.CASETYPE_RESTORE: {
		    let caseType = new CaseType({...action.caseType});
            st = state.clone();

            const existing = st.get(caseType.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (caseType.addedOn >= existing.addedOn) {
                    st.set(caseType);
                    return updateStorage(st, [caseType]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (caseType.status === 'D') return state;

                st.set(caseType);
                return updateStorage(st, [caseType]);
            }
        }
            
		case Actions.CASETYPE_SAVE: {
		    // console.debug('⮂ CASETYPE_SAVE reducer', action.caseType?.id);
		    
			// clone the caseType
			let caseType = new CaseType({...action.caseType});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new caseType in the state
			// If this is a create, add to the queue
			const _caseType = st.get(caseType.id);

			if (!_caseType) st.set(caseType);
			else {
				if (caseType.addedOn >= _caseType.addedOn) st.set(caseType);
			}

			return updateStorage(st, [caseType]);
		}

		case Actions.CASETYPE_SAVE_MANY: {
		    if (logging) console.debug('⮂ CASETYPE_SAVE_MANY reducer', action.caseTypes.map(caseType => caseType.id).join(','));
		    
			// clone the caseTypes
			let toProcess = action.caseTypes.map(caseType => new CaseType(caseType));

			st = state.clone();
			// state.clear();

			const updates: CaseType[] = [];

			toProcess.forEach(caseType => {
				// Add or update the new caseType in the state
				// If this is a create, add to the queue
				const existing = st.get(caseType.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (caseType.status !== 'D') {
						st.push(caseType);
						updates.push(caseType);
					}
					else if (logging) console.debug('⮂ CASETYPE_SAVE_MANY: skipping deleted caseType not in state');
				}
				else {
					if (caseType.addedOn >= existing.addedOn) {
						st.set(caseType);
						updates.push(caseType);
					}
					else if (logging) console.debug('⮂ Skipping, existing caseType had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CASETYPE_REMOVE: {
			// clone the caseType
			const caseType = new CaseType({...action.caseType});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(caseType);

			return updateStorage(st, [caseType]);
		}

		case Actions.CASETYPE_RESTORE_FAILURE:
		case Actions.CASETYPE_SAVE_FAILURE: {
		    console.warn('CASETYPE_SAVE_FAILURE reducer', action.caseType?.id);
		    if (!action.caseType?.id) {
				console.warn('CASETYPE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const caseType = ensure(CaseType, action.caseType);
			if (!caseType) {
				console.error('Could not find caseType after call to save');
				return state;
			}

			st = state.clone();

			const _caseTypeRef = state.get(caseType.id);

			if (_caseTypeRef && (_caseTypeRef.addedOn === caseType.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _caseType = new CaseType({..._caseTypeRef});
				_caseType.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_caseType.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('caseType', _caseType).then(r => {
						if (r.isErr()) console.error(`⮂ caseTypeReducer: failed to save caseType with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ caseTypeReducer: saved caseType with fatalError info');
					}).catch(err => console.error(`⮂ caseTypeReducer: failed to save caseType with fatalError info: ${err.message}`));

				st.set(_caseType);
			}

			return updateStorage(st);
		}

		case Actions.CASETYPE_SAVE_MANY_FAILURE: {
		    console.warn('CASETYPE_SAVE_MANY_FAILURE reducer', action.caseTypes.map(caseType => caseType.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.CASETYPE_RESTORE_SUCCESS:
		case Actions.CASETYPE_SAVE_SUCCESS: {
			if (!action.caseType?.id) {
				console.warn('CASETYPE_SAVE_SUCCESS reducer');
                return state;
			}

			const caseTypeRef = state.get(action.caseType?.id);
			if (!caseTypeRef) {
				console.warn('Could not find caseType after call to save');
                return state;
			}

			st = state.clone();

			if (action.caseType.addedOn >= caseTypeRef.addedOn) {
				st.set(action.caseType);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const caseType = new CaseType({...caseTypeRef, persisted: true});
				st.set(caseType);
			}

			return updateStorage(st, [action.caseType]);
		}

		case Actions.CASETYPE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ CASETYPE_SAVE_MANY_SUCCESS reducer', action.caseTypes.map(caseType => caseType.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const caseTypes: CaseType[] = [];

			action.caseTypes.forEach(e => {
				const caseTypeRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!caseTypeRef) console.warn('Could not find caseType after call to save');
				else if (e.addedOn >= caseTypeRef.addedOn) {
				    st.set(e);
				    caseTypes.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const caseType = new CaseType({...caseTypeRef, persisted: true});
					st.set(caseType);
					caseTypes.push(caseType);
				}
			});

			return updateStorage(st, caseTypes);
		}

		case Actions.CASETYPE_REMOVE_FAILURE: {
		    console.warn('CASETYPE_REMOVE_FAILURE action', action.caseType?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.CASETYPE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.CASETYPE_RESTORE_MANY: {
			if (logging) console.debug('⮂ CASETYPE_RESTORE_MANY reducer', action.caseTypes.map(caseType => caseType.id).join(','));

			const toProcess = action.caseTypes.map(caseType => new CaseType({...caseType}));
			st = state.clone();

			const updates: CaseType[] = [];

			toProcess.forEach(caseType => {
				const existing = st.get(caseType.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (caseType.addedOn >= existing.addedOn) {
						st.set(caseType);
						updates.push(caseType);
					}
					else if (logging) console.debug('⮂ CASETYPE_RESTORE_MANY: skipping older version of caseType');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (caseType.status !== 'D') {
						st.set(caseType);
						updates.push(caseType);
					}
					else if (logging) console.debug('⮂ CASETYPE_RESTORE_MANY: skipping deleted caseType not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CASETYPE_REMOVE_MANY: {
			if (logging) console.debug('⮂ CASETYPE_REMOVE_MANY reducer', action.caseTypes.map(caseType => caseType.id).join(','));

			const toProcess = action.caseTypes.map(caseType => new CaseType({...caseType}));
			st = state.clone();

			toProcess.forEach(caseType => {
				st.delete(caseType);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default caseTypes;